import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { environment } from 'src/environments/environment';
import { TRIAL_OFFER_QUERY_PARAM } from 'src/app/constants/trial-offer.constants';
import { GrowthBookService } from 'src/app/services/growthbook/service';

const reactRedirectGuards = new WeakSet<CanActivateFn>();

/** True for guards built by reactRedirectGuard (used by the routing spec). */
export function isReactRedirectGuard(guard: unknown): boolean {
  return typeof guard === 'function' && reactRedirectGuards.has(guard as CanActivateFn);
}

/**
 * Functional guard factory that redirects the current route to the React app's
 * /r<url> when the per-report cutover flag `react-cutover-<reportName>` is on for
 * this user/company. Off by default, so it's a no-op until the flag is flipped.
 *
 * Usage in routing:
 *   canActivate: [reactRedirectGuard('timeline')]   // checks react-cutover-timeline
 *
 * One boolean flag per report keeps rollouts independent (beta cohort -> all
 * users, or a percentage rollout per report) via GrowthBook targeting.
 *
 * environment.reactUrl is only needed when React is on a different origin than
 * Angular (local dev); in staging/prod it's empty since /r is same-origin behind
 * the load balancer.
 */
export function reactRedirectGuard(reportName: string): CanActivateFn {
    const guard: CanActivateFn = async (route, state) => {
    const growthbook = inject(GrowthBookService);
    const document = inject(DOCUMENT);
    await growthbook.initializedPromise;

    // The trial-offer dialog only exists in Angular, so its deep links must not be handed to React.
    if (route.queryParamMap.has(TRIAL_OFFER_QUERY_PARAM)) {
      return true;
    }

    if (growthbook.getFeatureValue(`react-cutover-${reportName}`, false)) {
      const base = environment.reactUrl.replace(/\/$/, '');
      document.location.href = base + '/r' + state.url;
      return false;
    }

    return true;
  };
  reactRedirectGuards.add(guard);
  return guard;
}
